import React from 'react';
import {debounce} from 'lodash';

import InputBox from '../InputBox/InputBox';
import Config from '../../utils/Configuration';
import Utility from '../../utils/Helper';
import {SearchIcon} from '../../styles/icons';

class GoogleAutoComplete extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            placePredictionsList: []
        };

        // eslint-disable-next-line no-underscore-dangle
        this._isMounted = false;
        this.googleServices = {};
        this.inputRef = React.createRef();
        this.googleAutoCompleteWidgetContainerClass = 'pac-container';
        this.isNormalMode = Config.googleAutoComplete.mode === Config.googleAutoComplete.modeOptions.NORMAL;
        this.isDebounceMode = Config.googleAutoComplete.mode === Config.googleAutoComplete.modeOptions.DEBOUNCE;

        this.inputFocusAction = this.inputFocusAction.bind(this);
        this.inputChangeHandler = this.inputChangeHandler.bind(this);
        this.requestPlaceDetails = this.requestPlaceDetails.bind(this);
        this.updateAutoCompletePredictions = this.updateAutoCompletePredictions.bind(this);
        this.requestAutoCompleteSuggestion = debounce(
            this.requestAutoCompleteSuggestion.bind(this),
            Config.googleAutoComplete.debouncePeriod
        );
    }

    componentDidMount() {
        // eslint-disable-next-line no-underscore-dangle
        this._isMounted = true;

        if (window.google) {
            if (typeof {...this.props.externalGoogleServiceConfig}.init === 'function') {
                this.googleServices = this.props.externalGoogleServiceConfig.init() || {};
            }

            if (this.isNormalMode && this.inputRef?.current) {
                const {componentRestrictions, types, fields} = Config.googleAutoComplete.requestProperties;
                const autoComplete = new window.google.maps.places.Autocomplete(this.inputRef.current, {
                    componentRestrictions,
                    types,
                    fields
                });

                setTimeout(() => {
                    const pacContainerElm =
                        document.getElementsByClassName(this.googleAutoCompleteWidgetContainerClass)[0] || {};
                    const originalClass = [...(pacContainerElm.classList || [])];
                    const pacContainerClass = this.props.pacContainerClass || '';

                    if (pacContainerClass && pacContainerElm && !originalClass.includes(pacContainerClass)) {
                        originalClass.push(pacContainerClass);
                        pacContainerElm.className = originalClass.join(' ');
                    }
                }, 500);

                autoComplete.addListener('place_changed', () => {
                    const place = autoComplete.getPlace();
                    if (!place.address_components) {
                        return;
                    }

                    const placeObj = Utility.getValues(autoComplete, 'gm_accessors_.place', {});
                    const formattedPredictionObj =
                        [...Object.entries(placeObj).find(([, value]) => value.formattedPrediction)][1] || {};

                    this.placeDetailsPostAction(place, formattedPredictionObj.formattedPrediction);
                });

                this.googleServices = {...this.googleServices, autoComplete};
            }
        }
    }

    componentWillUnmount() {
        // eslint-disable-next-line no-underscore-dangle
        this._isMounted = false;

        if (window.google) {
            Object.values(this.googleServices).forEach((service) => {
                window.google.maps.event.clearInstanceListeners(service);
            });

            this.isNormalMode &&
                [...document.getElementsByClassName(this.googleAutoCompleteWidgetContainerClass)].forEach((el) => {
                    el.remove();
                });
        }
    }

    updateAutoCompletePredictions(predictions = []) {
        // eslint-disable-next-line no-underscore-dangle
        this._isMounted && this.setState({placePredictionsList: predictions});
    }

    requestAutoCompleteSuggestion(keywords = '') {
        if (!window.google) return;

        if (keywords.length >= Config.googleAutoComplete.minPlaceSearchChar) {
            // google auto complete session period MAX 2 min
            const service = new window.google.maps.places.AutocompleteService();
            const savePredictionData = (predictions = [], status) => {
                let result = [];

                if (status && status === window.google.maps.places.PlacesServiceStatus.OK) {
                    result = predictions.map((prediction) => ({
                        id: prediction.place_id,
                        value: prediction,
                        highlightedText: Utility.getValues(prediction, 'structured_formatting.main_text', ''),
                        text: Utility.getValues(prediction, 'structured_formatting.secondary_text', '')
                    }));
                }
                this.updateAutoCompletePredictions(result);
            };

            const {componentRestrictions, types} = Config.googleAutoComplete.requestProperties;
            const predictionPayload = {componentRestrictions, types, input: keywords};

            if (this.googleServices.map) {
                // only can use this if got google map attached
                predictionPayload.bounds = this.googleServices.map.getBounds();
            }
            service.getPlacePredictions(predictionPayload, savePredictionData);
            return;
        }
        this.updateAutoCompletePredictions([]);
    }

    placeDetailsDataProcessor(response = {}) {
        const result = {...response};
        const addressObj = {};
        const supportedType = ['establishment', 'premise', 'point_of_interest'];
        const isPremise = supportedType.some((i) => result.types.includes(i));

        (result.address_components || []).forEach((component) => {
            addressObj[component.types[0]] = component.long_name;
        });

        const address1 = addressObj.route || '';
        const address2 = addressObj.sublocality_level_1 || '';
        const postcode = addressObj.postal_code || '';

        if (result.geometry.location) {
            result.geometry.location = {lat: result.geometry.location.lat(), lng: result.geometry.location.lng()};
        }

        return {
            addressObj,
            rawData: result,
            formattedData: {
                building: (isPremise && result.name) || '',
                addressLine1: address1,
                addressLine2: address2,
                postcode,
                address: [address1, address2].filter(Boolean).join(', ')
            },
            isPremise
        };
    }

    placeDetailsPostAction(response = {}, formattedAddressString = '') {
        const placeDetails = this.placeDetailsDataProcessor(response);
        placeDetails.formattedAddressString = formattedAddressString;
        const location = Utility.getValues(placeDetails, 'rawData.geometry.location');
        const {map: mapService, marker: markerService} = this.googleServices;

        location && mapService && mapService.panTo(location);
        location && markerService && markerService.setPosition(location);
        typeof this.props.onSelectSuggestion === 'function' && this.props.onSelectSuggestion(placeDetails);
    }

    requestPlaceDetails({place_id: placeId, description: formattedAddressString = ''} = {}) {
        if (!window.google) return;

        let service = this.googleServices.geocoder;

        if (!service) {
            service = new window.google.maps.Geocoder();
            this.googleServices.geocoder = service;
        }
        service.geocode({placeId}, (response, status) => {
            if (!status || status !== window.google.maps.GeocoderStatus.OK) return;

            this.placeDetailsPostAction(response && response[0], formattedAddressString);
        });
    }

    inputChangeHandler(event) {
        this.props.inputChangeHandler && this.props.inputChangeHandler(event);
        this.isDebounceMode && this.requestAutoCompleteSuggestion(event.target.value);
    }

    inputFocusAction(event) {
        typeof this.props.onFocus === 'function' && this.props.onFocus(event);
        !this.props.inputValue && this.state.placePredictionsList.length > 0 && this.updateAutoCompletePredictions([]);
    }

    render() {
        return (
            <InputBox
                {...this.props}
                inputRef={this.inputRef}
                onFocus={this.inputFocusAction}
                inputChangeHandler={this.inputChangeHandler}
                autoComplete={{
                    autoCompleteList: this.state.placePredictionsList,
                    autoCompleteLeadingIcon: <SearchIcon width='14px' height='14px' className='mx-2' />,
                    onAutoCompleteClick: this.requestPlaceDetails
                }}
            />
        );
    }
}

export default GoogleAutoComplete;
